import { useState } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import Layout from '../Layout'

// ---- Helpers (mirrors Transfer.jsx — extraction deferred) ----

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept':       'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  return { ok: res.ok, status: res.status, data: json }
}

// ---- Sub-components ----

function KindBadge({ kind }) {
  if (kind === 'personal') {
    return (
      <span className="px-2 py-1 rounded text-xs font-mono text-zinc-400 bg-zinc-800/60 border border-zinc-700">
        personal
      </span>
    )
  }
  return (
    <span className="px-2 py-1 rounded text-xs font-mono text-sky-400 bg-sky-950/30 border border-sky-800/30">
      company
    </span>
  )
}

function ConvertModal({ workspace, onClose }) {
  const [name,        setName]        = useState(workspace.name)
  const [submitting,  setSubmitting]  = useState(false)
  const [error,       setError]       = useState(null)

  async function handleConfirm() {
    setSubmitting(true)
    setError(null)

    const { ok, data } = await postJson(
      `/settings/workspaces/${workspace.id}/convert`,
      { workspace: { name } }
    )

    setSubmitting(false)

    if (!ok) {
      setError((data.errors || [data.error]).filter(Boolean).join(', '))
      return
    }

    router.visit('/settings/workspaces')
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full space-y-4">
        <h3 className="text-base font-mono font-bold text-zinc-100">
          Convert to company workspace
        </h3>

        <div className="border border-amber-800/50 rounded-lg p-3 bg-amber-950/20">
          <p className="text-xs font-mono text-amber-300 font-semibold">
            This action is irreversible.
          </p>
          <p className="text-xs font-mono text-amber-400 mt-1">
            Once converted, this workspace cannot be reverted to personal.
          </p>
        </div>

        <div>
          <label className="block text-xs font-mono text-zinc-400 mb-1">
            WORKSPACE NAME
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        {error && (
          <p className="text-xs font-mono text-red-400">{error}</p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-sky-700 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-sm font-medium py-2 px-4 rounded transition-colors"
          >
            {submitting ? 'Converting...' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Main component ----

export default function Index({ workspaces = [] }) {
  const { props } = usePage()
  const notice = props.flash?.notice
  const alert  = props.flash?.alert

  const [convertTarget, setConvertTarget] = useState(null)

  return (
    <Layout active="workspaces">
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-mono font-bold text-zinc-100">Workspaces</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Manage your workspaces.
            </p>
          </div>
          <Link
            href="/settings/workspaces/new"
            className="bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 font-mono text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            New workspace
          </Link>
        </div>

        {notice && (
          <div className="px-4 py-2 bg-emerald-950 border border-emerald-800 rounded-lg text-sm text-emerald-400 font-mono">
            {notice}
          </div>
        )}

        {alert && (
          <div className="px-4 py-2 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400 font-mono">
            {alert}
          </div>
        )}

        <section className="border border-zinc-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="border-b border-zinc-700 bg-zinc-800/40">
                <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700">
              {workspaces.map(w => (
                <tr key={w.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3 text-zinc-200">
                    <div className="flex items-center gap-2">
                      {w.active && (
                        <span className="text-emerald-400" title="Active workspace">●</span>
                      )}
                      {w.name}
                      <KindBadge kind={w.kind} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {w.kind === 'personal' && w.is_owner && (
                        <button
                          type="button"
                          onClick={() => setConvertTarget(w)}
                          className="px-2 py-1 rounded text-xs text-sky-400 bg-sky-950/30 hover:bg-sky-900/50 hover:text-sky-200 border border-sky-800/30 transition-colors"
                        >
                          Convert to company
                        </button>
                      )}
                      {w.is_owner && w.kind === 'company' && (
                        <Link
                          href={`/settings/workspaces/${w.id}/transfer`}
                          className="px-2 py-1 rounded text-xs text-amber-400 bg-amber-950/30 hover:bg-amber-900/50 hover:text-amber-200 border border-amber-800/30 transition-colors"
                        >
                          Transfer
                        </Link>
                      )}
                      <Link
                        href={`/settings/workspaces/${w.id}/edit`}
                        className="px-2 py-1 rounded text-xs text-zinc-400 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
                      >
                        Edit
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {workspaces.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-4 py-6 text-center text-zinc-600">
                    No workspaces available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {convertTarget && (
        <ConvertModal
          workspace={convertTarget}
          onClose={() => setConvertTarget(null)}
        />
      )}
    </Layout>
  )
}
